import React, { useState } from "react";
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ShoppingCart, ArrowLeftRight, Receipt, ClipboardList, ClipboardCheck } from "lucide-react";
import { currentMonthKey, monthLabel, prevMonthKey, nextMonthKey } from "../../utils/month";
import ImportPanel from "./ImportPanel";
import TransferPanel from "./TransferPanel";
import InvoicePanel from "./InvoicePanel";
import StocktakePanel from "./StocktakePanel";
import TicketsPanel from "./TicketsPanel";

interface WarehouseRef { _id: string; name: string; }
type Tab = "import" | "transfer" | "invoice" | "stocktake" | "tickets";

const TABS: { key: Tab; label: string; icon: React.ElementType }[] = [
  { key: "import",    label: "Nhập hàng",     icon: ShoppingCart },
  { key: "transfer",  label: "Chuyển kho",    icon: ArrowLeftRight },
  { key: "invoice",   label: "Hoá đơn tháng", icon: Receipt },
  { key: "stocktake", label: "Tồn kho",       icon: ClipboardList },
  { key: "tickets",   label: "Phiếu kiểm",    icon: ClipboardCheck },
];

export default function WarehouseWorkspaceTabs({ warehouseId, allWarehouses }: {
  warehouseId: string; allWarehouses: WarehouseRef[];
}) {
  const [open, setOpen]   = useState(false);
  const [tab, setTab]     = useState<Tab>("import");
  const [month, setMonth] = useState(() => currentMonthKey());

  return (
    <div className="border-t border-zinc-100">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2 px-5 py-3 text-left hover:bg-amber-50 transition-colors group"
      >
        <ShoppingCart size={15} className="text-amber-600 flex-shrink-0" />
        <span className="flex-1 text-sm font-semibold text-zinc-700">Nhập hàng / Chuyển kho / Hoá đơn / Tồn kho / Phiếu kiểm</span>
        {open ? <ChevronUp size={15} className="text-zinc-400" /> : <ChevronDown size={15} className="text-zinc-400" />}
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-3">
          {/* Month navigator (dùng chung cho cả 4 tab) */}
          <div className="flex items-center justify-between">
            <button onClick={() => setMonth(m => prevMonthKey(m))} className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-400 touch-manipulation">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-bold text-zinc-800">{monthLabel(month)}</span>
            <button onClick={() => setMonth(m => nextMonthKey(m))} className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-400 touch-manipulation">
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Tab switcher */}
          <div className="flex items-center gap-1 border-b border-zinc-100 overflow-x-auto">
            {TABS.map(t => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors touch-manipulation
                    ${tab === t.key ? "border-amber-500 text-amber-700" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}
                >
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>

          {tab === "import"    && <ImportPanel warehouseId={warehouseId} month={month} />}
          {tab === "transfer"  && <TransferPanel warehouseId={warehouseId} month={month} allWarehouses={allWarehouses} />}
          {tab === "invoice"   && <InvoicePanel warehouseId={warehouseId} month={month} />}
          {tab === "stocktake" && <StocktakePanel warehouseId={warehouseId} month={month} />}
          {tab === "tickets"   && <TicketsPanel warehouseId={warehouseId} month={month} />}
        </div>
      )}
    </div>
  );
}
